/**
 * User Hooks (Application)
 *
 * React Query hooks for user operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/modules/auth/application/store';
import { AuthStatus } from '@/modules/auth/domain/types';

import { userRepository } from '../../infrastructure/repositories';

import type { User } from '../../domain/types';

// ============================================================================
// Query Keys
// ============================================================================

export const userKeys = {
  all: ['user'] as const,
  profile: () => [...userKeys.all, 'profile'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

const CURRENT_USER_POLLING_INTERVAL_MS = 1000;
const CURRENT_USER_MAX_POLL_ATTEMPTS = 10;

/**
 * Hook to get current user profile
 */
export function useCurrentUser() {
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);

  return useQuery({
    queryKey: userKeys.profile(),
    queryFn: async () => {
      const response = await userRepository.getCurrentUser();
      return response.user;
    },
    enabled: status === AuthStatus.Authenticated && !user,

    /**
     * Bounded polling:
     * Some auth flows may yield a valid token before the backend can immediately
     * return a hydrated user profile. We poll briefly, then stop.
     */
    refetchInterval: (query) => {
      const shouldPoll =
        status === AuthStatus.Authenticated &&
        !user &&
        query.state.status !== 'success' &&
        query.state.fetchFailureCount < CURRENT_USER_MAX_POLL_ATTEMPTS;

      return shouldPoll ? CURRENT_USER_POLLING_INTERVAL_MS : false;
    },

    staleTime: 5 * 60 * 1000, // 5 minutes
    initialData: user || undefined,

    onSuccess: (fetchedUser) => {
      // Ensure the auth store is hydrated when current-user was fetched via polling.
      if (fetchedUser) {
        useAuthStore.getState().setUser(fetchedUser);
      }
    },
  });
}

/**
 * Hook to update user profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: async (data: Partial<User>) => {
      const response = await userRepository.updateProfile(data);
      return response.user;
    },
    onSuccess: (user) => {
      // Update cache
      queryClient.setQueryData(userKeys.profile(), user);
      // Update auth store
      setUser(user);
    },
  });
}

/**
 * Hook to create a test call (POST /user/test-call)
 */
export function useCreateTestCall() {
  return useMutation({
    mutationFn: () => userRepository.createTestCall(),
  });
}

/**
 * Hook to end a test call (DELETE /user/test-call)
 */
export function useEndTestCall() {
  return useMutation({
    mutationFn: (channelId: string) => userRepository.endTestCall(channelId),
  });
}

/**
 * Hook to clear cache for the current user (POST /cache)
 */
export function useClearUserCache() {
  return useMutation({
    mutationFn: () => userRepository.clearUserCache(),
  });
}
